/**
 * 分格验证码输入（6 位数字）。
 * 抽出成组件是因为注册验证、找回密码重置都要用，交互细节必须一致：
 * 自动聚焦下一格、退格回上一格、整串粘贴自动铺开、方向键移动。
 */
import { useRef, type ClipboardEvent, type KeyboardEvent } from 'react';
import { cn } from '../../lib/utils';

export interface CodeInputProps {
  length?: number;
  value: string;
  onChange: (value: string) => void;
  label?: string;
  /** 儿童模式加大字号与格子尺寸 */
  kid?: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
}

export function CodeInput({
  length = 6,
  value,
  onChange,
  label = '验证码',
  kid = false,
  autoFocus = false,
  disabled = false,
}: CodeInputProps) {
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const digits = Array.from({ length }, (_, i) => value[i] ?? '');

  const commit = (next: string[]) => onChange(next.join('').slice(0, length));

  const handleChange = (index: number, raw: string) => {
    const cleaned = raw.replace(/\D/g, '');
    const next = [...digits];
    if (cleaned.length > 1) {
      for (let i = 0; i < cleaned.length && index + i < length; i++) {
        next[index + i] = cleaned[i] ?? '';
      }
    } else {
      next[index] = cleaned;
    }
    commit(next);
    const advance = index + Math.max(1, cleaned.length);
    if (cleaned && advance < length) inputsRef.current[advance]?.focus();
  };

  const handleKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      event.preventDefault();
      const next = [...digits];
      next[index - 1] = '';
      commit(next);
      inputsRef.current[index - 1]?.focus();
    }
    if (event.key === 'ArrowLeft' && index > 0) inputsRef.current[index - 1]?.focus();
    if (event.key === 'ArrowRight' && index < length - 1) inputsRef.current[index + 1]?.focus();
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const text = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (!text) return;
    event.preventDefault();
    commit(Array.from({ length }, (_, i) => text[i] ?? ''));
    inputsRef.current[Math.min(text.length, length - 1)]?.focus();
  };

  return (
    <div className="space-y-2">
      <span className={cn('block font-medium text-slate-700', kid ? 'text-lg' : 'text-sm')}>
        {label}
      </span>
      <div className="flex justify-between gap-2">
        {digits.map((digit, index) => (
          <input
            key={index}
            ref={(el) => {
              inputsRef.current[index] = el;
            }}
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={1}
            disabled={disabled}
            autoFocus={autoFocus && index === 0}
            aria-label={`${label}第 ${index + 1} 位`}
            className={cn(
              'w-full rounded-xl border text-center font-semibold text-slate-900',
              'focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100',
              'disabled:bg-slate-100',
              kid ? 'h-[64px] text-3xl' : 'h-14 text-2xl',
              'border-slate-300',
            )}
          />
        ))}
      </div>
    </div>
  );
}
